<script setup lang="ts">
import { CheckBadgeIcon, PlusCircleIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { useField, useFieldArray, useFormContext } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'

import type { ContactPhoneDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/ContactPhoneDynamic'
import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import CollapsableContent from '@/components/common/layout/CollapsableContent.vue'
import ButtonTertiary from '@/components/common/ui/buttons/ButtonTertiary.vue'
import FormSelect from '@/components/common/ui/inputs/FormSelect.vue'
import InputGrid from '@/components/common/ui/inputs/InputGrid.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import ThreedotActionsOnHover from '@/components/common/ui/inputs/ThreedotActionsOnHover.vue'
import ContextMenuItem from '@/components/common/ui/overlays/ContextMenuItem.vue'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import {
  hasMatchingAiPhone,
  normalizeAiPhones,
  resolveAiPhoneTypeValue,
} from '@/utilities/organizationAi'
import { translationFunctionDefault } from '@/utilities/translations'

interface SectionPhoneNumbersProps {
  addPhoneNumberDisabled?: boolean | undefined
  aiData?: null | TWOrganizationAIResponse | undefined
  checkPrimary?: ((phoneNumber: ContactPhoneDynamic) => boolean) | undefined
  inCreateContext?: boolean
  onSubmit?: (() => void) | undefined
}

const props = defineProps<SectionPhoneNumbersProps>()

const tfDefault = translationFunctionDefault()

const backendContantsStore = useBackendConstantsStore()
const { fetchPhoneNumberList } = backendContantsStore
const { phoneNumberList, phoneNumberOptionList } = storeToRefs(backendContantsStore)

// TODO: find right generic type
const { setFieldValue } = useFormContext()
const phoneNumberFieldArray = useFieldArray<ContactPhoneDynamic>('phone_nos')

const aiFilledFields = ref<Set<string>>(new Set())

const getAiFieldCount = (): number => {
  return aiFilledFields.value.size
}

const isAiFieldsStillVisible = (): boolean => {
  return getAiFieldCount() > 0
}

const acceptAIFields = (): void => {
  aiFilledFields.value.clear()
}

const handleAddPhoneNumber = (): void => {
  phoneNumberFieldArray.push({
    doctype: 'Contact Phone',
    is_primary_phone: phoneNumberFieldArray.fields.value.length === 0 ? 1 : 0,
    phone: '',
    tw_phone_type: '',
  })
}

const isPrimaryPhoneNumber = (phoneNumber: ContactPhoneDynamic): boolean => {
  if (props.checkPrimary) {
    return props.checkPrimary(phoneNumber)
  }
  // TODO: boolean or number value here? (make backend consistent)
  const phonePrimaryValue = 1

  return phoneNumber.is_primary_phone === phonePrimaryValue
}

//TODO: not trigger on every click, on value change, only
const handleSetAsPrimary = (phoneNumber?: string): void => {
  if (phoneNumber) {
    phoneNumberFieldArray.fields.value.forEach(
      (currentPhoneNumberField, currentPhoneNumberFieldIndex) => {
        if (currentPhoneNumberField.value.phone === phoneNumber) {
          setFieldValue(`phone_nos.${currentPhoneNumberFieldIndex}.is_primary_phone`, 1)
        } else {
          setFieldValue(`phone_nos.${currentPhoneNumberFieldIndex}.is_primary_phone`, 0)
        }
      },
    )

    props.onSubmit?.()
  }
}

const handleRemovePhoneNumber = (phoneNumberItemindex: number, submit = true): void => {
  const currentItem = phoneNumberFieldArray.fields.value[phoneNumberItemindex]

  if (currentItem) {
    const itemPrimaryValue = 1
    const currentItemPrimaryValue = currentItem.value.is_primary_phone
    const isCurrentItemPrimary = currentItemPrimaryValue === itemPrimaryValue

    const aiPathsToRemove = ['tw_phone_type', 'phone']

    aiPathsToRemove.forEach((fieldName) => {
      const fullPath = `phone_nos.${phoneNumberItemindex}.${fieldName}`
      aiFilledFields.value.delete(fullPath)
    })

    for (
      let phoneIndex = phoneNumberItemindex + 1;
      phoneIndex < phoneNumberFieldArray.fields.value.length;
      phoneIndex++
    ) {
      aiPathsToRemove.forEach((fieldName) => {
        const oldPath = `phone_nos.${phoneIndex}.${fieldName}`
        const newPath = `phone_nos.${phoneIndex - 1}.${fieldName}`

        if (aiFilledFields.value.has(oldPath)) {
          aiFilledFields.value.delete(oldPath)
          aiFilledFields.value.add(newPath)
        }
      })
    }

    phoneNumberFieldArray.remove(phoneNumberItemindex)

    if (isCurrentItemPrimary) {
      const firstItemPositionIndex = 0
      const firstItem = phoneNumberFieldArray.fields.value[firstItemPositionIndex]

      if (firstItem) {
        const firstItemPrimaryValue = firstItem.value.is_primary_phone
        if (firstItemPrimaryValue !== itemPrimaryValue) {
          setFieldValue(
            `phone_nos.${firstItemPositionIndex.toFixed()}.is_primary_phone`,
            itemPrimaryValue,
          )
        }
      }
    }

    if (submit) props.onSubmit?.()
  }
}

const clearAIFields = (): void => {
  for (
    let phoneIndex = phoneNumberFieldArray.fields.value.length - 1;
    phoneIndex >= 0;
    phoneIndex--
  ) {
    const basePath = `phone_nos.${phoneIndex}`
    const hasAiFields = ['tw_phone_type', 'phone'].some((fieldName) =>
      aiFilledFields.value.has(`${basePath}.${fieldName}`),
    )

    if (hasAiFields) {
      handleRemovePhoneNumber(phoneIndex, false)
    }
  }
}

const phoneNumberExistsAndFilled = (index: number): boolean => {
  const phone = phoneNumberFieldArray.fields.value[index]?.value
  if (!phone) return false

  return !!(phone.tw_phone_type ?? phone.phone)
}

const getPhoneTypeIdFromLabel = (label: string | undefined): string | undefined => {
  return resolveAiPhoneTypeValue(label, phoneNumberOptionList.value, phoneNumberList.value)
}

const fillFields = (): void => {
  const aiPhone = normalizeAiPhones(props.aiData?.phone)

  aiPhone.forEach(({ phone: phoneNumber, type }) => {
    const existingPhones = phoneNumberFieldArray.fields.value.map((phone) => phone.value.phone)
    if (hasMatchingAiPhone(existingPhones, phoneNumber)) return

    const targetIndex = phoneNumberFieldArray.fields.value.length

    phoneNumberFieldArray.push({
      doctype: 'Contact Phone',
      is_primary_phone: targetIndex === 0 ? 1 : 0,
      phone: '',
      tw_phone_type: '',
    })

    if (!phoneNumberExistsAndFilled(targetIndex)) {
      const phoneTypeId = getPhoneTypeIdFromLabel(type ?? undefined)

      const fields = [
        { path: `phone_nos.${targetIndex}.tw_phone_type`, value: phoneTypeId ?? '' },
        { path: `phone_nos.${targetIndex}.phone`, value: phoneNumber || '' },
      ]

      fields.forEach(({ path, value }) => {
        const { value: fieldValue } = useField(path)
        if (!fieldValue.value && value) {
          setFieldValue(path, value)
          aiFilledFields.value.add(path)
        }
      })
    }
  })
}

onMounted(async () => {
  await fetchPhoneNumberList()
  fillFields()
})

watch(
  () => props.aiData,
  (newVal) => {
    if (newVal) {
      clearAIFields()
      fillFields()
    }
  },
)

defineExpose({
  acceptAIFields,
  clearAIFields,
  getAiFieldCount,
  isAiFieldsStillVisible,
})
</script>

<template>
  <CollapsableContent :title="tfDefault('editRelationGroup.phoneNumbersSectionTitle')">
    <ThreedotActionsOnHover
      v-for="(currentPhoneNumber, currentPhoneNumberIndex) in phoneNumberFieldArray.fields.value"
      :key="currentPhoneNumber.key"
      menu-align="right"
      visibility="hover"
    >
      <InputGrid :columns="2" :data-testid="TEST_IDS_CREATE.sectionPhoneNumbers.inputGrid">
        <FormSelect
          :data-testid="TEST_IDS_CREATE.sectionPhoneNumbers.typeSelect"
          :is-filled-by-ai="
            aiFilledFields.has(`phone_nos.${currentPhoneNumberIndex}.tw_phone_type`)
          "
          :label="tfDefault('editRelationGroup.phoneTypeLabel')"
          :name="`phone_nos.${currentPhoneNumberIndex}.tw_phone_type`"
          on-white-bg
          :options="[phoneNumberOptionList]"
          searchable
          @blur="onSubmit"
          @manual-input="
            () => aiFilledFields.delete(`phone_nos.${currentPhoneNumberIndex}.tw_phone_type`)
          "
        />

        <TextInput
          :data-testid="TEST_IDS_CREATE.sectionPhoneNumbers.numberInput"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`phone_nos.${currentPhoneNumberIndex}.phone`)"
          :label="
            isPrimaryPhoneNumber(currentPhoneNumber.value)
              ? tfDefault('editRelationGroup.phoneNumberTitlePrimary')
              : tfDefault('editRelationGroup.phoneNumberTitle')
          "
          :name="`phone_nos.${currentPhoneNumberIndex}.phone`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`phone_nos.${currentPhoneNumberIndex}.phone`)"
        />
      </InputGrid>
      <template #actions>
        <ContextMenuItem
          v-if="!isPrimaryPhoneNumber(currentPhoneNumber.value)"
          :icon="CheckBadgeIcon"
          :text="tfDefault('editRelationGroup.phoneNumberSetPrimaryButton')"
          @click="() => handleSetAsPrimary(currentPhoneNumber.value.phone)"
        />
        <ContextMenuItem
          :icon="TrashIcon"
          :text="tfDefault('editRelationGroup.remove')"
          @click="() => handleRemovePhoneNumber(currentPhoneNumberIndex)"
        />
      </template>
    </ThreedotActionsOnHover>
    <ButtonTertiary
      :data-testid="TEST_IDS_CREATE.sectionPhoneNumbers.addButton"
      :disabled="!!addPhoneNumberDisabled"
      :label="tfDefault('editRelationGroup.addPhoneNumberButtonLabel')"
      @click="handleAddPhoneNumber"
    >
      <PlusCircleIcon class="mr-2 h-6 w-6" />
    </ButtonTertiary>
  </CollapsableContent>
</template>
